import type { FeedItem } from '../types/feedContract'

export type BetCardTreatmentFormat = 'compact_carousel' | 'hub_stack' | 'feed_story'

export const BET_CARD_TREATMENT_FORMATS_BY_DISPLAY_TYPE: Readonly<Record<string, readonly BetCardTreatmentFormat[]>> = Object.freeze({
  horizontal_carousel: ['compact_carousel'],
  story_block: ['feed_story', 'hub_stack'],
})

export interface BetCardTreatmentConfig {
  version: 1
  format: BetCardTreatmentFormat
  visible_legs: number
  stake_cents: number
  show_social_proof: boolean
  show_media: boolean
  show_rationale: boolean
  spotlight_first: boolean
}

export interface SurfaceRuntimeConfig extends Record<string, unknown> {
  title?: string
  title_highlight?: string
  destination_label?: string
  bet_card_treatment?: Partial<BetCardTreatmentConfig>
  visible_item_limit?: number
}

export const DEFAULT_BET_CARD_TREATMENT: Readonly<BetCardTreatmentConfig> = Object.freeze({
  version: 1,
  format: 'compact_carousel',
  visible_legs: 3,
  stake_cents: 1000,
  show_social_proof: true,
  show_media: false,
  show_rationale: true,
  spotlight_first: false,
})

const BET_CARD_FORMATS: Readonly<Record<BetCardTreatmentFormat, true>> = Object.freeze({
  compact_carousel: true,
  hub_stack: true,
  feed_story: true,
})

const VISIBLE_LEG_LIMITS: Readonly<Record<BetCardTreatmentFormat, readonly [number, number]>> = Object.freeze({
  compact_carousel: [2, 3],
  feed_story: [2, 3],
  hub_stack: [4, 6],
})

function boundedInteger(
  value: unknown,
  fallback: number,
  minimum: number,
  maximum: number,
): number {
  const numberValue = typeof value === 'number' ? value : Number(value)
  if (!Number.isSafeInteger(numberValue)) return fallback
  return Math.min(maximum, Math.max(minimum, numberValue))
}

function booleanValue(value: unknown, fallback: boolean): boolean {
  return typeof value === 'boolean' ? value : fallback
}

export function allowedBetCardTreatmentFormats(
  displayType: string | null | undefined,
): readonly BetCardTreatmentFormat[] {
  return displayType ? BET_CARD_TREATMENT_FORMATS_BY_DISPLAY_TYPE[displayType] || [] : []
}

export function defaultBetCardTreatmentFormat(
  displayType: string | null | undefined,
): BetCardTreatmentFormat | null {
  return allowedBetCardTreatmentFormats(displayType)[0] || null
}

export function betCardTreatmentFormatIsCompatible(
  displayType: string | null | undefined,
  format: BetCardTreatmentFormat,
): boolean {
  return allowedBetCardTreatmentFormats(displayType).includes(format)
}

export function betCardVisibleLegOptions(format: BetCardTreatmentFormat): number[] {
  const [minimum, maximum] = VISIBLE_LEG_LIMITS[format]
  return Array.from({ length: maximum - minimum + 1 }, (_, index) => minimum + index)
}

function normalizedVisibleLegs(format: BetCardTreatmentFormat, value: unknown): number {
  const [minimum, maximum] = VISIBLE_LEG_LIMITS[format]
  const fallback = format === 'hub_stack' ? 5 : DEFAULT_BET_CARD_TREATMENT.visible_legs
  return boundedInteger(value, fallback, minimum, maximum)
}

export function normalizeBetCardTreatmentConfig(
  config: Record<string, unknown> | null | undefined,
): BetCardTreatmentConfig | null {
  const raw = config?.bet_card_treatment
  if (!raw || typeof raw !== 'object' || Array.isArray(raw)) return null

  const candidate = raw as Record<string, unknown>
  if (Number(candidate.version) !== 1) return null

  const format = typeof candidate.format === 'string' && BET_CARD_FORMATS[candidate.format as BetCardTreatmentFormat]
    ? candidate.format as BetCardTreatmentFormat
    : DEFAULT_BET_CARD_TREATMENT.format

  return {
    version: 1,
    format,
    visible_legs: normalizedVisibleLegs(format, candidate.visible_legs),
    stake_cents: boundedInteger(candidate.stake_cents, DEFAULT_BET_CARD_TREATMENT.stake_cents, 100, 100_000),
    show_social_proof: booleanValue(candidate.show_social_proof, DEFAULT_BET_CARD_TREATMENT.show_social_proof),
    show_media: booleanValue(candidate.show_media, DEFAULT_BET_CARD_TREATMENT.show_media),
    show_rationale: booleanValue(candidate.show_rationale, DEFAULT_BET_CARD_TREATMENT.show_rationale),
    spotlight_first: booleanValue(candidate.spotlight_first, DEFAULT_BET_CARD_TREATMENT.spotlight_first),
  }
}

/**
 * Resolves an optional surface treatment into the only projection that is safe
 * for the current layout. Surface membership remains entirely explicit: this
 * setting changes how qualifying multi-leg bets look, never which posts appear.
 */
export function resolveBetCardTreatmentForSurface(
  config: Record<string, unknown> | null | undefined,
  displayType: string | null | undefined,
): BetCardTreatmentConfig | null {
  const treatment = normalizeBetCardTreatmentConfig(config)
  if (!treatment) return null

  const fallbackFormat = defaultBetCardTreatmentFormat(displayType)
  if (!fallbackFormat) return null
  if (betCardTreatmentFormatIsCompatible(displayType, treatment.format)) return treatment

  return {
    ...treatment,
    format: fallbackFormat,
    visible_legs: normalizedVisibleLegs(fallbackFormat, treatment.visible_legs),
  }
}

export function isMultiLegBetItem(item: FeedItem | null | undefined): boolean {
  // Tickets keep their own design; FeedCard/CompactCard dispatch them.
  if (item?.content_origin === 'winning_ticket') return false
  const selectionSet = item?.selection_set
  const selectionCount = selectionSet?.summary?.selection_count
    ?? selectionSet?.selections?.length
    ?? item?.legs?.length
    ?? 0
  if (selectionCount < 2) return false

  const summaryKind = selectionSet?.summary?.kind
  if (summaryKind && ['parlay', 'same_game_parlay', 'mixed', 'provider_defined'].includes(summaryKind)) {
    return true
  }

  return selectionSet?.entries?.some((entry) => (
    ['parlay', 'same_game_parlay', 'mixed', 'provider_defined'].includes(entry.kind)
    && entry.selection_ids.length > 1
  )) ?? Boolean(item?.legs && item.legs.length > 1)
}

